import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';

const STATUT_COLORS = { brouillon: '#9e9e9e', emise: '#1565c0', payee_partiellement: '#f57c00', payee: '#2e7d32', annulee: '#e53935' };
const STATUT_LABELS = { brouillon: 'Brouillon', emise: 'Emise', payee_partiellement: 'Partielle', payee: 'Payée', annulee: 'Annulée' };

const EMPTY = { client_id: '', numero: '', objet: '', date_echeance: '', montant_ht: '', montant_ttc: '' };

export default function Facturation() {
  const [factures, setFactures] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEscapeClose(showForm, () => closeForm());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [fRes, cRes] = await Promise.all([
        api.get('/factures'),
        api.get('/clients').catch(() => ({ data: [] })),
      ]);
      setFactures(fRes.data);
      setClients(cRes.data);
    } catch { setError('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeForm = () => { setShowForm(false); setForm(EMPTY); setEditId(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editId) {
        await api.put(`/factures/${editId}`, form);
      } else {
        await api.post('/factures', form);
      }
      closeForm();
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const handleEdit = (f) => {
    setForm({
      client_id: f.client_id || '', numero: f.numero, objet: f.objet,
      date_echeance: f.date_echeance?.split('T')[0] || '',
      montant_ht: f.montant_ht || '', montant_ttc: f.montant_ttc || '',
      statut: f.statut,
    });
    setEditId(f.id);
    setShowForm(true);
  };

  const handleStatut = async (id, statut) => {
    try {
      await api.put(`/factures/${id}`, { statut });
      load();
    } catch {}
  };

  const setField = (k, v) => {
    setForm(f => {
      const next = { ...f, [k]: v };
      if (k === 'montant_ht') {
        next.montant_ttc = (parseFloat(v) || 0).toFixed(2);
      }
      return next;
    });
  };

  const totaux = factures.reduce((acc, f) => {
    acc.total += Number(f.montant_ttc) || 0;
    acc.paye += Number(f.montant_paye) || 0;
    return acc;
  }, { total: 0, paye: 0 });

  return (
    <div>
{error && <div className="gss-alert gss-alert-error">{error}<button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>&times;</button></div>}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Total factures</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg></div></div>
          <div className="stat-card-value">{factures.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Montant total</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></div></div>
          <div className="stat-card-value">{totaux.total.toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Encaisse</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg></div></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-success)' }}>{totaux.paye.toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Reste a percevoir</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-warning)' }}>{(totaux.total - totaux.paye).toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
      </div>

      <div className="content-card">
        <div className="gss-toolbar">
          <div className="content-card-title">Liste des factures</div>
          <button className="gss-btn gss-btn-primary" onClick={() => { setForm(EMPTY); setEditId(null); setShowForm(true); }}>
            + Nouvelle facture
          </button>
        </div>
        {loading ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
        ) : factures.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDDDDD" strokeWidth="1.5" style={{ marginBottom: 16 }}>
              <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
            </svg>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#666', marginBottom: 4 }}>Aucune facture enregistree</div>
            <div style={{ fontSize: 14, color: '#999' }}>Cliquez sur le bouton pour commencer</div>
          </div>
        ) : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr><th>Numero</th><th>Client</th><th>Objet</th><th>Date</th><th>Montant TTC</th><th>Paye</th><th>Statut</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {factures.map(f => (
                  <tr key={f.id}>
                    <td><strong>{f.numero}</strong></td>
                    <td>{f.client_nom || '-'}</td>
                    <td>{f.objet}</td>
                    <td>{f.date_facture?.split('T')[0]}</td>
                    <td>{Number(f.montant_ttc).toLocaleString()} FCFA</td>
                    <td>{Number(f.montant_paye).toLocaleString()} FCFA</td>
                    <td>
                      <span className="gss-badge" style={{ background: (STATUT_COLORS[f.statut] || '#999') + '20', color: STATUT_COLORS[f.statut] }}>
                        {STATUT_LABELS[f.statut] || f.statut}
                      </span>
                    </td>
                    <td>
                      <div className="gss-actions">
                        <button className="gss-btn-icon" onClick={() => handleEdit(f)} title="Modifier">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        {f.statut === 'emise' && (
                          <button className="gss-btn-icon" onClick={() => handleStatut(f.id, 'payee')} title="Marquer payée" style={{ color: '#2e7d32' }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="gss-modal-overlay" onClick={closeForm}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div>
                <h2>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                  </svg>
                  {editId ? 'Modifier la facture' : 'Nouvelle facture'}
                </h2>
                <div className="gss-modal-subtitle">Facturation client</div>
              </div>
              <button className="gss-modal-close" onClick={closeForm}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-modal-section">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/></svg>
                  <span>IDENTIFICATION</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Numero *</label>
                    <input className="gss-input" required value={form.numero} onChange={e => setField('numero', e.target.value)} placeholder="FAC-2026-001" />
                  </div>
                  <div className="gss-field">
                    <label>Client *</label>
                    <select className="gss-input" required value={form.client_id} onChange={e => setField('client_id', e.target.value)}>
                      <option value="">-- Selectionner --</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.raison_sociale} — [{c.statut_commercial}]</option>)}
                    </select>
                  </div>
                  <div className="gss-field gss-field-full">
                    <label>Objet *</label>
                    <input className="gss-input" required value={form.objet} onChange={e => setField('objet', e.target.value)} placeholder="Prestation de gardiennage..." />
                  </div>
                  <div className="gss-field">
                    <label>Date echeance</label>
                    <input className="gss-input" type="date" value={form.date_echeance} onChange={e => setField('date_echeance', e.target.value)} />
                  </div>
                  {editId && (
                    <div className="gss-field">
                      <label>Statut</label>
                      <select className="gss-input" value={form.statut} onChange={e => setField('statut', e.target.value)}>
                        <option value="brouillon">Brouillon</option>
                        <option value="emise">Emise</option>
                        <option value="payee_partiellement">Payee partiellement</option>
                        <option value="payee">Payee</option>
                        <option value="annulee">Annulee</option>
                      </select>
                    </div>
                  )}
                </div>

                <div className="gss-modal-section">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                  <span>MONTANTS</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Montant (FCFA) *</label>
                    <input className="gss-input" type="number" step="0.01" required value={form.montant_ht} onChange={e => setField('montant_ht', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Montant TTC</label>
                    <input className="gss-input" type="number" step="0.01" value={form.montant_ttc} readOnly style={{ background: '#f5f5f5' }} />
                  </div>
                  <div className="gss-field">
                    <span style={{ fontSize: '0.78rem', color: '#888', fontStyle: 'italic' }}>Exonere de TVA — Regime simplifie</span>
                  </div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={closeForm}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>
                  {saving ? 'Enregistrement...' : (
                    <>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                      {editId ? 'Mettre a jour' : 'Creer la facture'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
